import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  ArrowRight,
  CheckCircle,
  Search,
  ChevronDown,
  Sparkles,
  Wallet
} from 'lucide-react';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/ui/PageHeader';
import { useApp } from '../../context/AppContext';
import { useBroker } from '../../context/BrokerContext';
import './BankSelection.css';

function BankSelection() {
  const navigate = useNavigate();
  const {
    currentTransaction,
    setCurrentTransaction,
    PAKISTAN_BANKS,
    setUserType
  } = useApp();
  const { getBrokerPath } = useBroker();

  const [selectedBank, setSelectedBank] = useState(null);
  const [searchBank, setSearchBank] = useState('');
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!currentTransaction || !currentTransaction.userCnic) {
      navigate(getBrokerPath('/'));
    }
  }, [currentTransaction, navigate, getBrokerPath]);

  const filteredBanks = PAKISTAN_BANKS.filter(bank =>
    bank.name.toLowerCase().includes(searchBank.toLowerCase()) ||
    bank.code.toLowerCase().includes(searchBank.toLowerCase())
  );

  const handleBankSelect = (bank) => {
    setSelectedBank(bank);
    setSearchBank('');
    setShowBankDropdown(false);
    setErrors(prev => ({ ...prev, bank: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedBank) {
      setErrors({ bank: 'Please select your bank' });
      return;
    }

    setIsLoading(true);

    setTimeout(() => {
      setUserType('new');
      setCurrentTransaction({
        ...currentTransaction,
        selectedBank: selectedBank,
        bankCode: selectedBank.code,
        bankName: selectedBank.name,
        bankType: selectedBank.type, // 'wallet' or 'bank'
        isReturningUser: false,
      });
      navigate(getBrokerPath('/iban-verification'));
      setIsLoading(false);
    }, 500);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.bank-dropdown-container')) {
        setShowBankDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  if (!currentTransaction) {
    return null;
  }

  return (
    <div className="bank-container animate-fade-in">
      {/* Unified Main Card */}
      <div className="bank-main-card">
        {/* Header Section */}
        <PageHeader showBackButton={false} greeting="Select your bank" />

        {/* Content Section */}
        <div className="bank-content">
          {/* Section Header */}
          <div className="section-header">
            <Building2 size={18} />
            <span>Bank Selection</span>
          </div>

          <form onSubmit={handleSubmit} className="bank-form">
            {/* Bank Selection Dropdown */}
            <div className="bank-dropdown-container">
              <label className="input-label">Select Bank</label>
              <div
                className={`bank-dropdown-trigger ${showBankDropdown ? 'active' : ''} ${errors.bank ? 'has-error' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowBankDropdown(!showBankDropdown);
                }}
              >
                <div className="bank-dropdown-value">
                  {selectedBank ? (
                    <div className="selected-bank-display">
                      <div className="bank-logo-wrapper small">
                        {selectedBank.logo ? (
                          <img
                            src={selectedBank.logo}
                            alt={selectedBank.name}
                            className="bank-logo"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className="bank-logo-fallback" style={{ display: selectedBank.logo ? 'none' : 'flex' }}>
                          {selectedBank.type === 'wallet' ? <Wallet size={18} /> : <Building2 size={18} />}
                        </div>
                      </div>
                      <span>{selectedBank.name}</span>
                    </div>
                  ) : (
                    <span className="placeholder">Choose your bank</span>
                  )}
                </div>
                <ChevronDown size={20} className={`dropdown-arrow ${showBankDropdown ? 'rotated' : ''}`} />
              </div>

              {showBankDropdown && (
                <div className="bank-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                  <div className="bank-search">
                    <Search size={18} />
                    <input
                      type="text"
                      placeholder="Search bank..."
                      value={searchBank}
                      onChange={(e) => setSearchBank(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="bank-list">
                    {filteredBanks.map((bank) => (
                      <div
                        key={bank.code}
                        className={`bank-option ${selectedBank?.code === bank.code ? 'selected' : ''}`}
                        onClick={() => handleBankSelect(bank)}
                      >
                        <div className="bank-logo-wrapper">
                          {bank.logo ? (
                            <img
                              src={bank.logo}
                              alt={bank.name}
                              className="bank-logo"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className="bank-logo-fallback" style={{ display: bank.logo ? 'none' : 'flex' }}>
                            {bank.type === 'wallet' ? <Wallet size={20} /> : <Building2 size={20} />}
                          </div>
                        </div>
                        <div className="bank-info">
                          <span className="bank-name">{bank.name}</span>
                          <span className="bank-code">{bank.code}</span>
                        </div>
                        {selectedBank?.code === bank.code && <CheckCircle size={18} className="check-icon" />}
                      </div>
                    ))}
                    {filteredBanks.length === 0 && (
                      <div className="no-results">No banks found</div>
                    )}
                  </div>
                </div>
              )}
              {errors.bank && <span className="input-error">{errors.bank}</span>}
            </div>

            <Button
              type="submit"
              variant="primary"
              size="large"
              fullWidth
              loading={isLoading}
              icon={ArrowRight}
              iconPosition="right"
              disabled={!selectedBank}
            >
              Continue
            </Button>
          </form>

          {/* Process Steps */}
          <div className="process-section">
            <div className="process-title">Whitelisting Steps</div>
            <div className="process-steps">
              <div className="step active">
                <div className="step-indicator">
                  <CheckCircle size={16} />
                </div>
                <span>Select Bank</span>
              </div>
              <div className="step-connector"></div>
              <div className="step">
                <div className="step-indicator">2</div>
                <span>Enter IBAN</span>
              </div>
              <div className="step-connector"></div>
              <div className="step">
                <div className="step-indicator">3</div>
                <span>Verify OTP</span>
              </div>
              <div className="step-connector"></div>
              <div className="step">
                <div className="step-indicator">4</div>
                <span>Approval</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Section */}
        <div className="bank-footer">
          <div className="footer-title">
            <Sparkles size={14} />
            <span>Quick & Secure Process</span>
          </div>
          <p className="footer-text">Your bank details are encrypted and securely stored</p>
        </div>
      </div>
    </div>
  );
}

export default BankSelection;
